import { z } from 'zod';

import { zodAddressSchema } from '@vestigas/data';

import { EmailSchema } from '@vestigas/utils';

import { DATA_EXCHANGE_STATUS } from './constants';

export const zodBlockerSchema = z.object({
  count: z.number().int().nullable(),
  kind: z.string(),
});

/**
 * `{emails}` list returned by the invoice-allowed-sender-address and
 * invoice-mailbox GET endpoints (`EmailList` in the OpenAPI spec).
 */
export const zodEmailListResponseSchema = z.object({
  emails: z.array(EmailSchema),
});

/**
 * Body for `POST /company/identifier/` (`AddCompanyIdentifier` in the
 * OpenAPI spec) — maps a customer number to a company. Shared by every
 * caller of that endpoint: the DNPv2 parsing-case flow
 * (`useMutationMapDlnPartyId`), the old asset-reparsing flow
 * (`useMutationMapCompanyId`), and the direct company-identifier UI
 * (`useMutationCompanyAddIdentifier`).
 */
export const zodAddCompanyIdentifierPayloadSchema = z.object({
  identifiedCompanyId: z.uuid(),
  identifyingText: z.string().trim().min(1),
  issuerCompanyId: z.uuid(),
});

/**
 * Minimal company info nested in a `ReturnCompanyAliasAddress` response
 * (`CompanyBasicInfo` in the OpenAPI spec).
 */
export const zodCompanyBasicInfoSchema = z.object({
  address: zodAddressSchema.nullish(),
  id: z.uuid(),
  name: z.string(),
});

/**
 * Full alias-address shape returned by the alias-address create/update/get/
 * list endpoints (`ReturnCompanyAliasAddress` in the OpenAPI spec).
 */
export const zodCompanyAliasAddressSchema = z.object({
  address: zodAddressSchema.nullish(),
  comment: z.string().nullish(),
  company: zodCompanyBasicInfoSchema,
  companyId: z.uuid(),
  createdBy: z.string().nullish(),
  createdById: z.uuid(),
  createdOn: z.string(),
  id: z.uuid(),
  invoiceAddress: z.boolean().prefault(false),
  issuerCompany: z.array(z.string()).prefault([]),
  issuerId: z.array(z.uuid()).prefault([]),
  modifiedBy: z.string().nullish(),
  modifiedById: z.uuid(),
  modifiedOn: z.string(),
  name: z.string(),
  restrictAddress: z.boolean(),
});

export const zodSupplierLetterMetaSchema = z.object({
  downloadUrl: z.string().nullish(),
  fileId: z.uuid(),
  fileName: z.string(),
  mimeType: z.string(),
  uploadedAt: z.iso.datetime({ offset: true }).nullable(),
});

const zodNotionTargetModeSchema = z.enum(['explicit', 'expanded']);

/**
 * Document type used when generating a DLN PDF attached to an invoice email
 * (`vgscommon__pdf_defaults__PdfDlnRequestDoctype` in the OpenAPI spec).
 */
export const zodPdfDlnRequestDoctypeSchema = z.enum([
  'docx',
  'original',
  'original_enhanced',
]);

/**
 * Fields maintained by the Notion supplier sync. Read-only: not editable
 * through any of the vadmin company write endpoints.
 */
export const zodCompanySyncDataSchema = z.object({
  dlnFormat: z.array(z.string()).nullable(),
  dlnIncomingChannel: z.array(z.string()).nullable(),
  invFormat: z.array(z.string()).nullable(),
  notionSyncedById: z.uuid().nullable(),
  notionSyncedOn: z.iso.datetime().nullable(),
  notionTargetMode: zodNotionTargetModeSchema.nullable(),
  parserInfoDln: z.array(z.string()).nullable(),
  parserInfoInv: z.array(z.string()).nullable(),
  specialCases: z.array(z.string()).nullable(),
  unlockDetails: z.string().nullable(),
});

const zodDataExchangeStatusSchema = z.enum(DATA_EXCHANGE_STATUS);

const zodAlertConfigSchema = z.object({
  delayedDays: z.number(),
  numDays: z.number(),
  onlyWorkDays: z.boolean(),
});

/**
 * Full company shape returned by the single-company GET endpoint
 * (`CompanyInternalResponse` in the OpenAPI spec) and reused by the
 * create/update/match endpoints, which return the same shape.
 *
 * Mirrors the backend's CompanyResponse, which serves unset fields as null
 * rather than omitting them. `name` and `companyAccountId` are nullable
 * there too; the nameless companies seen so far came from the list endpoint,
 * and relaxing `name` here ripples into the company forms and mutation
 * payloads — tracked separately.
 */
export const zodCompanyAdminSchema = z.object({
  address: zodAddressSchema.nullable(),
  alertConfig: zodAlertConfigSchema.nullable(),
  blacklisted: z.boolean(),
  companyAccountId: z.uuid(),
  companyAccountName: z.string().nullable(),
  createdOn: z.iso.datetime({ offset: true }).nullable(),
  currentIssues: z.string().nullable(),
  furtherInformation: z.string().nullable(),
  gln: z.string().nullable(),
  hra: z.string().nullish(),
  hraRegisterCourt: z.string().nullish(),
  hrb: z.string().nullable(),
  hubspotDealId: z.string().nullish(),
  id: z.uuid(),
  interchangeGln: z.string().nullish(),
  isActive: z.boolean(),
  isReceiver: z.boolean(),
  isSender: z.boolean(),
  knownIssues: z.string().nullable(),
  logoUrl: z.string().nullable(),
  name: z.string(),
  notFeasibleReason: z.string().nullable(),
  rank: z.number(),
  registerCourt: z.string().nullish(),
  statusDln: zodDataExchangeStatusSchema,
  statusInvoice: zodDataExchangeStatusSchema,
  syncData: zodCompanySyncDataSchema.optional(),
  syncKey: z.string().nullable(),
  vatId: z.string().nullable(),
});
